import React, { useState, useCallback, useEffect } from 'react';
import { ApplicationEnum } from '@landr/core.models';
import { FeatureFlag } from 'types';
import { EventId } from 'enum/eventId';
import createFlags from 'flag';
import { log } from '../../log';
import { APP_VERSION } from '../../constants';

interface DevToolHooks {
    emit: (name: string, payload: any) => void;
    on: (name: string, callback: Function) => () => void;
}

declare global {
    interface Window {
        __LANDR_DEVTOOLS_HOOKS__?: DevToolHooks;
    }
}

const getDevToolHooks = (): DevToolHooks | null =>
    (typeof window !== 'undefined' && window && window.document && window.__LANDR_DEVTOOLS_HOOKS__) || null;

/**
 * Send an event to LANDR devtools with the initial application details
 */
export function setupLandrDevTools() {
    const devToolHooks = getDevToolHooks();
    if (devToolHooks) {
        devToolHooks.emit('set-state', {
            name: ApplicationEnum.GuestSite,
            version: APP_VERSION,
            config: {
                /**
                 * Pass 1 property to validate it works well.
                 * TODO: https://mixgenius.atlassian.net/browse/ECO-303
                 */
                GATSBY_MONOLITH_ENDPOINT: process.env.GATSBY_MONOLITH_ENDPOINT,
            },
        });
    }
}

export type FeatureFlags = {
    [FeatureFlag.ExperimentFeaturedSavingPlan]?: any;
};

const { FlagsProvider: InternalFlagsProvider, Flag, useFlag, useFlags } = createFlags<FeatureFlags>();

const FlagsProvider: React.FC = ({ children }) => {
    const [featureFlags, setFeatureFlags] = useState<FeatureFlags>({});

    const handleDevtoolFeatureFlagClick = useCallback(
        ({ payload: forcedFlags }: { payload: Partial<FeatureFlags> }) => {
            setFeatureFlags({
                ...featureFlags,
                ...forcedFlags,
            });
        },
        [featureFlags],
    );

    // Update devtools when featureFlags state changes
    useEffect(() => {
        const devToolHooks = getDevToolHooks();
        if (devToolHooks) {
            devToolHooks.emit('set-state', {
                featureFlags: featureFlags,
            });
        }
    }, [featureFlags]);

    useEffect(() => {
        try {
            fetch(`${process.env.GATSBY_KUBERNETES_ENDPOINT}/featureflag/api/v2/featureflag`, {
                headers: {
                    'Content-Type': 'application/json',
                    'X-LANDR-Client': `Version=${APP_VERSION}, Kind=${ApplicationEnum.Web}`,
                },
                credentials: 'include',
            })
                .then(response =>
                    response.json().then(featureFlagsFromApi =>
                        setFeatureFlags({
                            ...featureFlags,
                            ...{ [FeatureFlag.ExperimentFeaturedSavingPlan]: false },
                            ...featureFlagsFromApi,
                        }),
                    ),
                )
                .catch(() => {
                    setFeatureFlags({
                        ...featureFlags,
                        ...{ [FeatureFlag.ExperimentFeaturedSavingPlan]: false },
                    });
                });

            const devToolHooks = getDevToolHooks();
            if (devToolHooks) {
                const destroyHandler = devToolHooks.on('feature-flags-updated', handleDevtoolFeatureFlagClick);

                return destroyHandler;
            }
        } catch (error) {
            log.error('Feature flags failed', EventId.FeatureFlagsFailed, error);
        }
    }, []);

    return <InternalFlagsProvider flags={featureFlags}> {children}</InternalFlagsProvider>;
};

setupLandrDevTools();

export { FlagsProvider, Flag, useFlag, useFlags };
